import { FC, ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useAuth from 'src/hooks/useAuth';
import ForbiddenPage from 'src/pages/ForbiddenPage';

interface UserAdministratorGuardProps {
  children?: ReactNode;
}

/**
 * Route guard for the ENG-2139 Compliance User Administration tool.
 *
 * Mirrors the backend IsUserAdministrator permission exactly: superusers,
 * members of the 'User Administrator' group, and members of 'Django User - Eng'
 * pass. Anyone else gets the Forbidden page.
 *
 * Unauthenticated users are redirected to `/` (login) and their current URL
 * is stashed in `nextUrl` so they land back on /user-management after signing
 * in — matching the behavior of RoleBasedGuard.
 *
 * We intentionally do not fold superuser support into the generic
 * RoleBasedGuard because the other routes guarded by RoleBasedGuard (resident,
 * compliance, pronto-admin, etc.) have different group-vs-superuser semantics.
 */
const UserAdministratorGuard: FC<UserAdministratorGuardProps> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(
        'nextUrl',
        `${location?.pathname || ''}${location?.search || ''}`
      );
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After login completes, consume the stashed nextUrl so the user
  // lands back on /user-management instead of whatever the login flow
  // defaults to. Mirrors the second effect in RoleBasedGuard — without
  // it, writing nextUrl above is a no-op for this guard.
  useEffect(() => {
    const nextUrl = localStorage.getItem('nextUrl');
    if (!nextUrl) {
      return;
    }
    const currentUrl = `${location?.pathname || ''}${location?.search || ''}`;
    if (
      isAuthenticated &&
      nextUrl !== '/' &&
      Boolean(currentUrl) &&
      nextUrl !== currentUrl
    ) {
      navigate(nextUrl);
      localStorage.removeItem('nextUrl');
    }
  }, [isAuthenticated, location, navigate]);

  if (!isAuthenticated || !user) {
    return <></>;
  }

  if (!user.isUserAdministrator) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
};

export default UserAdministratorGuard;
